import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SystemNotificationCenterService } from '../../../core/services/system-notification-center.service';
import { SystemNotificationItem } from '../../../shared/models/system-notification-center';
import { MatDialog } from '@angular/material/dialog';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { AlertType } from '../../../shared/models/alertType';

interface NotificationGroup {
  label: string;
  items: SystemNotificationItem[];
}

@Component({
  selector: 'app-notification-center-panel',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './notification-center-panel.component.html',
  styleUrl: './notification-center-panel.component.scss',
})
export class NotificationCenterPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLElement>;

  isOpen = false;
  isLoading = false;
  unreadCount = 0;
  notifications: SystemNotificationItem[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly notificationCenterService: SystemNotificationCenterService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.notificationCenterService.initialize();

    this.notificationCenterService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
      this.isOpen = isOpen;
    });

    this.notificationCenterService.loading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this.notificationCenterService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
      this.unreadCount = count;
    });

    this.notificationCenterService.notifications$.pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
      this.notifications = notifications;
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.notificationCenterService.closePanel();
  }

  markAllAsRead(): void {
    this.notificationCenterService.markAllAsRead();
  }

  onScroll(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }

    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining < 120) {
      this.notificationCenterService.loadNextPage();
    }
  }

  openNotification(notification: SystemNotificationItem): void {
    if (!notification.isRead) {
      this.notificationCenterService.markAsRead(notification.id);
    }

    if (notification.consultantId && notification.consultantId > 0) {
      this.close();
      this.router.navigate(['/relatorios/execucao'], {
        queryParams: {
          mode: 'Realtime',
          consultantId: notification.consultantId,
        },
      });
      return;
    }

    if (notification.consultantId === 0) {
      showAlertDialog(
        this.dialog,
        AlertType.Warning,
        'Conteúdo indisponível',
        'O conteúdo referenciado nesta notificação não está mais disponível.',
      );
    }
  }

  get groupedNotifications(): NotificationGroup[] {
    const groups = new Map<string, SystemNotificationItem[]>();

    this.notifications.forEach((notification) => {
      const label = this.formatGroupLabel(notification.createdAt);
      const current = groups.get(label) ?? [];
      groups.set(label, [...current, notification]);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  }

  badgeLabel(): string {
    if (this.unreadCount > 99) {
      return '99+';
    }

    return String(this.unreadCount);
  }

  get hasUnreadNotifications(): boolean {
    return this.unreadCount > 0 || this.notifications.some((notification) => !notification.isRead);
  }

  trackByNotificationId(_: number, notification: SystemNotificationItem): number {
    return notification.id;
  }

  metadataLabel(createdAt: string): string {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;

    if (diffMs < hourMs) {
      const minutes = Math.max(1, Math.floor(diffMs / minuteMs));
      return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    }

    if (diffMs < 12 * hourMs) {
      const hours = Math.max(1, Math.floor(diffMs / hourMs));
      return `há ${hours} hora${hours > 1 ? 's' : ''}`;
    }

    return `${createdDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  }

  private formatGroupLabel(createdAt: string): string {
    const date = new Date(createdAt);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (this.isSameDay(date, today)) {
      return 'Hoje';
    }

    const formattedDate = date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    if (this.isSameDay(date, yesterday)) {
      return `Ontem, ${formattedDate}`;
    }

    return formattedDate;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
}
